import { createFlatProxy } from './createProxy.mjs';
import { defaultFormatter } from './error/formatter.mjs';
import { createMiddlewareFactory } from './middleware.mjs';
import { createBuilder } from './procedureBuilder.mjs';
import { isServerDefault } from './rootConfig.mjs';
import { createRouterFactory, mergeRouters, createCallerFactory } from './router.mjs';
import { getDataTransformer, defaultTransformer } from './transformer.mjs';

/**
 * TODO: This can be improved:
 * - We should be able to chain `.meta()`/`.context()` only once
 * - Simplify typings
 * - Doesn't need to be a class but it doesn't really hurt either
 */ class TRPCBuilder {
    /**
   * Add a context shape as a generic to the root object
   * @link https://trpc.io/docs/v11/server/context
   */ context() {
        return new TRPCBuilder();
    }
    /**
   * Add a meta shape as a generic to the root object
   * @link https://trpc.io/docs/v11/quickstart
   */ meta() {
        return new TRPCBuilder();
    }
    /**
   * Create the root object
   * @link https://trpc.io/docs/v11/server/routers#initialize-trpc
   */ create(options) {
        return createTRPCInner()(options);
    }
}
/**
 * Builder to initialize the tRPC root object - use this exactly once per backend
 * @link https://trpc.io/docs/v11/quickstart
 */ const initTRPC = new TRPCBuilder();
function createTRPCInner() {
    return function initTRPCInner(runtime) {
        const errorFormatter = runtime?.errorFormatter ?? defaultFormatter;
        const transformer = getDataTransformer(runtime?.transformer ?? defaultTransformer);
        const config = {
            transformer,
            isDev: runtime?.isDev ?? // eslint-disable-next-line @typescript-eslint/dot-notation
            globalThis.process?.env?.['NODE_ENV'] !== 'production',
            allowOutsideOfServer: runtime?.allowOutsideOfServer ?? false,
            errorFormatter,
            isServer: runtime?.isServer ?? isServerDefault,
            /**
       * @internal
       */ $types: createFlatProxy((key)=>{
                throw new Error(`Tried to access "$types.${key}" which is not available at runtime`);
            })
        };
        {
            // Server check
            const isServer = runtime?.isServer ?? isServerDefault;
            if (!isServer && runtime?.allowOutsideOfServer !== true) {
                throw new Error(`You're trying to use @trpc/server in a non-server environment. This is not supported by default.`);
            }
        }
        return {
            /**
       * These are just types, they can't be used
       * @internal
       */ _config: config,
            /**
       * Builder object for creating procedures
       * @link https://trpc.io/docs/v11/server/procedures
       */ procedure: createBuilder({
                meta: runtime?.defaultMeta
            }),
            /**
       * Create reusable middlewares
       * @link https://trpc.io/docs/v11/server/middlewares
       */ middleware: createMiddlewareFactory(),
            /**
       * Create a router
       * @link https://trpc.io/docs/v11/server/routers
       */ router: createRouterFactory(config),
            /**
       * Merge Routers
       * @link https://trpc.io/docs/v11/server/merging-routers
       */ mergeRouters,
            /**
       * Create a server-side caller for a router
       * @link https://trpc.io/docs/v11/server/server-side-calls
       */ createCallerFactory: createCallerFactory()
        };
    };
}

export { initTRPC };
